import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { jobCreateSchema } from '@fin/shared/schemas';
import {
  JOB_PRIORITIES,
  JOB_STATUSES,
  SERVICE_TYPES,
  SERVICE_LABELS,
  STATUS_LABELS,
  type JobStatus,
} from '@fin/shared/enums';
import { BRAND } from '@fin/shared/brand';
import { dubaiLocalToISO } from '@fin/shared/time';
import { buildWhatsAppLink, type WhatsAppTemplateId } from '@fin/shared/whatsapp';

import {
  Button,
  Card,
  Field,
  Modal,
  PageHeader,
} from '../components/ui';
import { PageIcon, PageAccent } from '../components/PageIcons';
import {
  inputClass,
  useDisclosure,
} from '../components/ui';
import {
  useCreateJob,
  useCustomers,
  useJobs,
  usePatchJob,
  useWorkers,
  uploadProofForJob,
  type Customer,
  type Job,
  type Worker,
} from '../lib/queries';

const STATUS_TABS: JobStatus[] = [
  'scheduled',
  'assigned',
  'on_my_way',
  'in_progress',
  'completed',
  'cancelled',
];

export function JobsPage() {
  const [params, setParams] = useSearchParams();
  const tab = ((JOB_STATUSES as readonly string[]).includes(params.get('status') ?? '')
    ? (params.get('status') as JobStatus)
    : 'scheduled');
  const search = params.get('q') ?? '';
  const workerFilter = params.get('worker') ?? '';

  const updateParam = (key: string, value: string) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };
  const setTab = (s: JobStatus) => updateParam('status', s === 'scheduled' ? '' : s);
  const setSearch = (v: string) => updateParam('q', v);
  const setWorkerFilter = (v: string) => updateParam('worker', v);

  // Terminal statuses accumulate forever — offer a date window so admins can
  // reach history beyond the first page.
  const terminalTab = tab === 'completed' || tab === 'cancelled';
  const rangeKey = params.get('range') ?? (terminalTab ? '90d' : 'all');
  // Always persist the choice — clearing the param would fall back to the
  // '90d' default below, making "All time" impossible to select.
  const setRangeKey = (v: string) => updateParam('range', v);
  const range = useMemo(() => {
    const days = rangeKey === '7d' ? 7 : rangeKey === '30d' ? 30 : rangeKey === '90d' ? 90 : null;
    if (!days) return undefined;
    return { from: new Date(Date.now() - days * 86_400_000).toISOString() };
  }, [rangeKey]);

  const list = useJobs(tab, terminalTab ? range : undefined);
  const allJobs = useMemo(
    () => (list.data?.pages ?? []).flatMap((pg) => pg.items),
    [list.data],
  );
  const customers = useCustomers();
  const workers = useWorkers();
  const modal = useDisclosure();
  const patch = usePatchJob();

  const customerById = useMemo(
    () => Object.fromEntries((customers.data ?? []).map((c) => [c.id, c])),
    [customers.data],
  );
  const workerById = useMemo(
    () => Object.fromEntries((workers.data ?? []).map((w) => [w.id, w])),
    [workers.data],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allJobs.filter((j) => {
      const ids = j.assignedWorkerIds && j.assignedWorkerIds.length > 0
        ? j.assignedWorkerIds
        : j.assignedWorkerId
          ? [j.assignedWorkerId]
          : [];
      if (workerFilter === '__none__' && ids.length > 0) return false;
      if (workerFilter && workerFilter !== '__none__' && !ids.includes(workerFilter)) return false;
      if (!q) return true;
      const cust = customerById[j.customerId];
      const workerNames = ids.map((id) => workerById[id]?.name ?? '').join(' ').toLowerCase();
      return (
        cust?.name?.toLowerCase().includes(q) ||
        cust?.phone?.toLowerCase().includes(q) ||
        cust?.address?.area?.toLowerCase().includes(q) ||
        workerNames.includes(q) ||
        j.serviceType.toLowerCase().includes(q)
      );
    });
  }, [allJobs, search, workerFilter, customerById, workerById]);

  return (
    <>
      <PageHeader
        title="Jobs"
        subtitle="Schedule, dispatch and track every visit"
        icon={PageIcon.jobs}
        accent={PageAccent.jobs}
        action={
          <div className="flex gap-2">
            <Button
              variant="white"
              onClick={() => exportJobsCsv(filtered, customerById, workerById, tab)}
              disabled={filtered.length === 0}
            >
              ↓ Export CSV
            </Button>
            <Button variant="white" onClick={modal.onOpen}>+ Create job</Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap gap-1.5 sm:gap-2">
        {STATUS_TABS.map((s) => {
          const active = tab === s;
          return (
            <button
              key={s}
              onClick={() => setTab(s)}
              className="whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium shadow-sm transition hover:shadow"
              style={{
                backgroundColor: active ? BRAND.primary : 'white',
                color: active ? 'white' : BRAND.text,
                border: `1px solid ${active ? BRAND.primary : BRAND.border}`,
              }}
            >
              {s.replace('_', ' ')}
            </button>
          );
        })}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search customer / phone / area / worker / service…"
          className={`${inputClass} max-w-sm`}
        />
        <select
          className={`${inputClass} max-w-xs`}
          value={workerFilter}
          onChange={(e) => setWorkerFilter(e.target.value)}
        >
          <option value="">All workers</option>
          <option value="__none__">Unassigned</option>
          {(workers.data ?? []).map((w) => (
            <option key={w.id} value={w.id}>{w.name}</option>
          ))}
        </select>
        {terminalTab && (
          <select
            className={`${inputClass} max-w-[11rem]`}
            value={rangeKey}
            onChange={(e) => setRangeKey(e.target.value)}
            title="Limit by scheduled date"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        )}
        {(search || workerFilter) && (
          <button
            type="button"
            onClick={() => { setSearch(''); setWorkerFilter(''); }}
            className="text-xs text-stone-500 hover:underline"
          >
            Clear
          </button>
        )}
      </div>

      {list.isPending && <p className="text-stone-500">Loading…</p>}
      {list.isError && <p style={{ color: BRAND.danger }}>Failed to load: {(list.error as Error).message}</p>}
      {list.data && (
        <Card>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="border-b border-stone-200 text-left text-xs uppercase tracking-wide text-stone-500">
              <tr>
                <th className="px-4 py-3" title="Scheduled date and time (Asia/Dubai)">When</th>
                <th className="px-4 py-3" title="Click the customer name to open the job">Customer</th>
                <th className="px-4 py-3" title="Service category for this job">Service</th>
                <th className="px-4 py-3" title="Assigned worker. If unassigned, pick one to dispatch the job.">Worker</th>
                <th className="px-4 py-3" title="Job priority (urgent jobs surface in dashboards)">Priority</th>
                <th className="px-4 py-3" title="Override the job's lifecycle status. Workers normally drive these transitions from the field app — use this to correct or force a state.">Status</th>
                <th className="px-4 py-3" title="Open WhatsApp with a pre-filled message to the customer (no message is sent automatically)">WhatsApp</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((j: Job) => {
                const cust = customerById[j.customerId];
                const w = j.assignedWorkerId ? workerById[j.assignedWorkerId] : undefined;
                return (
                  <tr key={j.id} className="border-b border-stone-100 align-top last:border-0">
                    <td className="px-4 py-3 text-stone-700">
                      {j.anytime
                        ? `${new Date(j.scheduledAt).toLocaleDateString('en-AE', {
                            timeZone: 'Asia/Dubai',
                            dateStyle: 'medium',
                          })} · Anytime`
                        : new Date(j.scheduledAt).toLocaleString('en-AE', {
                            timeZone: 'Asia/Dubai',
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      <Link to={`/jobs/${j.id}`} className="hover:underline" style={{ color: BRAND.primary }}>
                        {cust?.name ?? j.id.slice(-6)}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-stone-600">
                      {SERVICE_LABELS[j.serviceType as keyof typeof SERVICE_LABELS]?.en ?? j.serviceType}
                    </td>
                    <td className="px-4 py-3">
                      {w ? (
                        <span title={(j.assignedWorkerIds ?? [j.assignedWorkerId].filter(Boolean) as string[]).map((id) => workerById[id]?.name ?? id.slice(-6)).join(', ')}>
                          {w.name}
                          {(j.assignedWorkerIds?.length ?? 0) > 1 && (
                            <span
                              className="ml-1 inline-flex items-center justify-center rounded-full px-1.5 text-[10px] font-semibold"
                              style={{ backgroundColor: BRAND.primary, color: 'white' }}
                            >
                              +{(j.assignedWorkerIds!.length) - 1}
                            </span>
                          )}
                        </span>
                      ) : (
                        <select
                          className="rounded border border-stone-300 px-2 py-1 text-xs"
                          defaultValue=""
                          title="Assign a worker to dispatch this job"
                          onChange={(e) =>
                            patch.mutate({ id: j.id, body: { assignedWorkerId: e.target.value } })
                          }
                        >
                          <option value="" disabled>
                            Assign…
                          </option>
                          {(workers.data ?? []).filter((w) => w.active).map((w) => (
                            <option key={w.id} value={w.id}>
                              {w.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-4 py-3 text-stone-600">{j.priority}</td>
                    <td className="px-4 py-3">
                      <select
                        className="rounded border border-stone-300 px-2 py-1 text-xs"
                        value={j.status}
                        title="Override status — use only if the worker can’t update from the field"
                        onChange={(e) =>
                          patch.mutate({ id: j.id, body: { status: e.target.value as JobStatus } })
                        }
                      >
                        {JOB_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {STATUS_LABELS[s]?.en ?? s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <WhatsAppCell job={j} customer={cust} workerName={w?.name} />
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-stone-500">
                    {allJobs.length === 0
                      ? terminalTab && rangeKey !== 'all'
                        ? `No ${tab} jobs in this date range — try “All time”.`
                        : `No jobs in '${tab.replaceAll('_', ' ')}'.`
                      : 'No jobs match the current filter.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
          {list.hasNextPage && (
            <div className="flex justify-center border-t border-stone-100 py-3">
              <Button
                variant="white"
                onClick={() => void list.fetchNextPage()}
                disabled={list.isFetchingNextPage}
              >
                {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </Card>
      )}

      <Modal open={modal.open} onClose={modal.onClose} title="Create job">
        <CreateJobForm onClose={modal.onClose} />
      </Modal>
    </>
  );
}

function CreateJobForm({ onClose }: { onClose: () => void }) {
  const customers = useCustomers();
  const workers = useWorkers();
  const create = useCreateJob();
  const [customerId, setCustomerId] = useState('');
  const [serviceType, setServiceType] = useState<(typeof SERVICE_TYPES)[number]>('plumbing');
  const [priority, setPriority] = useState<(typeof JOB_PRIORITIES)[number]>('normal');
  const [scheduledAt, setScheduledAt] = useState('');
  const [anytime, setAnytime] = useState(false);
  const [duration, setDuration] = useState(60);
  const [description, setDescription] = useState('');
  const [assignedWorkerIds, setAssignedWorkerIds] = useState<string[]>([]);
  const [intakeFiles, setIntakeFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function addIntakeFiles(files: FileList | null) {
    if (!files) return;
    const next: File[] = [];
    for (const f of Array.from(files)) {
      if (f.type.startsWith('video/') && f.size > 75 * 1024 * 1024) {
        setError(`Video "${f.name}" exceeds 75 MB — skipped.`);
        continue;
      }
      next.push(f);
    }
    setIntakeFiles((cur) => [...cur, ...next]);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      // datetime-local is entered as Asia/Dubai wall time. When "Anytime" is
      // set we only keep the date and anchor the time to 08:00 Dubai so the
      // job sorts to the start of its working day.
      const localValue = anytime ? `${scheduledAt.slice(0, 10)}T08:00` : scheduledAt;
      const body = jobCreateSchema.parse({
        customerId,
        serviceType,
        priority,
        scheduledAt: dubaiLocalToISO(localValue),
        anytime,
        estimatedDurationMinutes: Number(duration),
        description: description || undefined,
        assignedWorkerIds: assignedWorkerIds.length > 0 ? assignedWorkerIds : undefined,
      });
      const job = await create.mutateAsync(body);
      // Upload intake media after the job exists so we have a real jobId
      // for the S3 key. Failures are reported but don't block job creation
      // — the admin can re-upload from the job detail page.
      if (intakeFiles.length > 0) {
        for (let i = 0; i < intakeFiles.length; i += 1) {
          const f = intakeFiles[i];
          if (!f) continue;
          setUploadProgress(`Uploading ${i + 1} / ${intakeFiles.length}…`);
          const kind = f.type.startsWith('video/') ? 'intake_video' : 'intake_photo';
          try {
            await uploadProofForJob(job.id, f, kind, 'Customer-supplied at intake');
          } catch (uerr) {
            console.warn('Intake upload failed for', f.name, uerr);
          }
        }
        setUploadProgress(null);
      }
      onClose();
    } catch (err) {
      setUploadProgress(null);
      setError(err instanceof z.ZodError ? err.issues.map((i) => i.message).join(', ') : err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      {error && (
        <div
          role="alert"
          className="rounded border px-3 py-2 text-xs"
          style={{ borderColor: BRAND.danger, color: BRAND.danger, backgroundColor: '#FEF2F2' }}
        >
          {error}
        </div>
      )}
      <Field label="Customer">
        <select
          className={inputClass}
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          required
        >
          <option value="" disabled>
            Choose a customer…
          </option>
          {(customers.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} — {c.phone}
            </option>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Service">
          <select
            className={inputClass}
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value as (typeof SERVICE_TYPES)[number])}
          >
            {SERVICE_TYPES.map((s) => (
              <option key={s} value={s}>
                {SERVICE_LABELS[s].en}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Priority">
          <select
            className={inputClass}
            value={priority}
            onChange={(e) => setPriority(e.target.value as (typeof JOB_PRIORITIES)[number])}
          >
            {JOB_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {/* Anytime toggle — kept ABOVE the When field so the native date-picker
          dropdown can't cover it. When on, the time is ignored. */}
      <label
        className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border px-3 py-2.5"
        style={{
          borderColor: anytime ? BRAND.primary : BRAND.border,
          backgroundColor: anytime ? '#F0FDF4' : 'white',
        }}
      >
        <span>
          <span className="text-sm font-medium" style={{ color: BRAND.text }}>Anytime</span>
          <span className="block text-xs text-stone-500">
            Customer is flexible / home all day — no specific time slot
          </span>
        </span>
        <input
          type="checkbox"
          className="h-5 w-5"
          checked={anytime}
          onChange={(e) => setAnytime(e.target.checked)}
        />
      </label>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={anytime ? 'Day' : 'When'} hint={anytime ? 'Pick the day — the time is ignored.' : undefined}>
          <input
            className={inputClass}
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            required
          />
        </Field>
        <Field label="Estimated duration (min)">
          <input
            className={inputClass}
            type="number"
            min={15}
            max={720}
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
          />
        </Field>
      </div>
      <Field
        label="Assign workers (optional)"
        hint={`Up to 5 workers. The first worker is the primary (gets WhatsApp + reminders).${assignedWorkerIds.length > 0 ? ` ${assignedWorkerIds.length} selected.` : ''}`}
      >
        <div className="flex flex-wrap gap-1.5">
          {(workers.data ?? []).filter((w) => w.active).map((w) => {
            const idx = assignedWorkerIds.indexOf(w.id);
            const selected = idx !== -1;
            const isPrimary = idx === 0;
            const atCap = !selected && assignedWorkerIds.length >= 5;
            return (
              <button
                key={w.id}
                type="button"
                disabled={atCap}
                onClick={() => {
                  setAssignedWorkerIds((cur) =>
                    selected
                      ? cur.filter((id) => id !== w.id)
                      : cur.length >= 5
                        ? cur
                        : [...cur, w.id],
                  );
                }}
                className="rounded-full px-3 py-1 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  backgroundColor: selected ? BRAND.primary : 'white',
                  color: selected ? 'white' : BRAND.text,
                  border: `1px solid ${selected ? BRAND.primary : BRAND.border}`,
                }}
                title={isPrimary ? 'Primary worker' : selected ? 'Selected' : atCap ? 'Max 5 workers' : 'Add to job'}
              >
                {isPrimary && '★ '}
                {w.name}
              </button>
            );
          })}
          {(workers.data ?? []).filter((w) => w.active).length === 0 && (
            <span className="text-xs text-stone-500">No active workers.</span>
          )}
        </div>
      </Field>
      <Field label="Description (optional)">
        <textarea
          className={inputClass}
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <Field
        label="Customer photos / video (optional)"
        hint="Up to 75 MB per video. The worker will see these in the job before they arrive."
      >
        <input
          className="block w-full text-xs text-stone-700"
          type="file"
          accept="image/*,video/mp4,video/quicktime,video/webm"
          multiple
          onChange={(e) => {
            addIntakeFiles(e.target.files);
            e.target.value = '';
          }}
        />
        {intakeFiles.length > 0 && (
          <ul className="mt-2 space-y-1 text-xs">
            {intakeFiles.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center justify-between rounded bg-stone-50 px-2 py-1">
                <span className="truncate text-stone-700">
                  {f.type.startsWith('video/') ? '🎥' : '🖼️'} {f.name}{' '}
                  <span className="text-stone-400">({(f.size / 1024 / 1024).toFixed(1)} MB)</span>
                </span>
                <button
                  type="button"
                  onClick={() => setIntakeFiles((cur) => cur.filter((_, idx) => idx !== i))}
                  className="text-xs text-stone-500 hover:text-stone-700"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </Field>
      {uploadProgress && (
        <p className="text-xs text-stone-500">{uploadProgress}</p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={create.isPending || uploadProgress !== null}>
          {create.isPending ? 'Saving…' : uploadProgress ? 'Uploading…' : 'Create job'}
        </Button>
      </div>
    </form>
  );
}

/**
 * Renders a tiny dropdown of WhatsApp click-to-send templates appropriate
 * for the current job + customer (per §24). Tapping opens WhatsApp on the
 * staff member's device with the message pre-drafted.
 */
function WhatsAppCell({
  job,
  customer,
  workerName,
}: {
  job: Job;
  customer?: { name: string; phone: string; whatsappPhone?: string };
  workerName?: string;
}) {
  if (!customer) return <span className="text-stone-400">—</span>;
  const time = new Date(job.scheduledAt).toLocaleTimeString('en-AE', {
    timeZone: 'Asia/Dubai',
    hour: '2-digit',
    minute: '2-digit',
  });
  const service =
    SERVICE_LABELS[job.serviceType as keyof typeof SERVICE_LABELS]?.en ?? job.serviceType;

  function open(template: WhatsAppTemplateId) {
    const url = buildWhatsAppLink(customer!.whatsappPhone ?? customer!.phone, template, {
      customerName: customer!.name,
      workerName,
      serviceType: service,
      scheduledTime: time,
      eta: time,
      feedbackUrl: `${import.meta.env.VITE_FEEDBACK_BASE_URL ?? 'https://feedback.fixitninjaz.com'}/r/${job.id}`,
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  return (
    <select
      className="rounded border px-2 py-1 text-xs"
      style={{ borderColor: '#25D366', backgroundColor: '#E6F8EE', color: '#075E54' }}
      defaultValue=""
      title="Open WhatsApp with a pre-filled message for this job. Nothing is sent until you press Send in WhatsApp."
      onChange={(e) => {
        const v = e.target.value as WhatsAppTemplateId | '';
        if (v) open(v);
        e.target.value = '';
      }}
    >
      <option value="" disabled>
        Send WhatsApp…
      </option>
      <option value="customer_job_assigned">Job assigned</option>
      <option value="customer_on_the_way">On the way</option>
      <option value="customer_job_started">Job started</option>
      <option value="customer_job_completed">Completed + feedback</option>
    </select>
  );
}


function exportJobsCsv(
  jobs: Job[],
  customerById: Record<string, Customer>,
  workerById: Record<string, Worker>,
  tab: string,
) {
  const tz = 'Asia/Dubai';
  const fmtDt = (s?: string) => {
    if (!s) return '';
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
      }).format(new Date(s));
    } catch { return s; }
  };
  const esc = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const header = ['Scheduled', 'Customer', 'Phone', 'Service', 'Worker', 'Priority', 'Status'];
  const rows = jobs.map((j) => {
    const c = customerById[j.customerId];
    const w = j.assignedWorkerId ? workerById[j.assignedWorkerId] : undefined;
    return [
      fmtDt(j.scheduledAt),
      c?.name ?? '',
      c?.phone ?? '',
      j.serviceType ?? '',
      w?.name ?? '',
      j.priority ?? '',
      j.status,
    ].map(esc).join(',');
  });
  const csv = [header.join(','), ...rows].join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const date = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `fin-jobs-${tab}-${date}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
